/**
 * UPT-2124 J4a (spec `web/SPEC-UPT-2124-J4.md` §2 n° 4) — la TABLE DES RISQUES de « Géode pilote l'interface » : trois niveaux, liste fermée.
 *
 * - `free` (libre) : joué après le fantôme (naviguer, lire, défiler, ouvrir un onglet / une carte / une ligne, Suivant, Précédent, Fermer, Annuler).
 * - `confirm` (confirmé) : carte de confirmation J2d + « oui » vocal ou clic — tout bouton qui écrit côté serveur (Enregistrer, Sauvegarder, Valider,
 *   Envoyer, Soumettre, Créer, Produire, Lancer, Accepter une proposition d'analyse…).
 * - `forbidden` (interdit à la voix) : Géode répond « Ce geste se fait à l'écran » et met le bouton en évidence (Supprimer, Effacer, Écarter,
 *   Archiver, Révoquer, Payer, Acheter, Déposer dans un dépôt, Accepter une version du modèle, Approuver une porte, Promouvoir, Se déconnecter,
 *   changer de locataire, modifier des droits, modifier une clé ou un secret), plus tout bouton `data-voice="forbidden"` ou situé dans une `Modal`
 *   de variante `destructive`.
 *
 * Les motifs sont des RACINES de mots (déjà normalisées : minuscules, sans accents) dans les cinq langues (fr, en, de, it, ar) ; le nom accessible
 * d'un élément est comparé à TOUTES les langues (l'interface et la voix peuvent différer). Un motif est une liste de groupes : chaque groupe doit
 * trouver un mot qui commence par l'une de ses racines (`stem$` = mot entier ; l'arabe, à préfixes collés, cherche la sous-chaîne).
 *
 * Pur : aucune lecture de l'écran ici, que du texte (la lecture de l'élément est dans `uiActor`).
 */

export type UiRisk = 'free' | 'confirm' | 'forbidden';

/** Pourquoi un élément a ce niveau (tests, journal, cartes). */
export type UiRiskReason = 'data-voice' | 'modal' | 'table' | 'submit' | 'touched-form' | 'default';

/** Un motif : chaque groupe (OU de racines) doit trouver un mot du nom (ET entre les groupes). */
export type UiRiskPattern = readonly (readonly string[])[];

export type UiRiskLangTable = { forbidden: readonly UiRiskPattern[]; confirm: readonly UiRiskPattern[] };

/** Minuscules, sans accents latins ni voyelles brèves arabes, alef unifiés, apostrophes droites, espaces réduits. */
export const normalizeUiText = (text: string): string =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[\u0610-\u061a\u064b-\u065f\u0670\u0640]/g, '')
    .replace(/[\u0622\u0623\u0625]/g, '\u0627')
    .toLowerCase()
    .replace(/[\u2019\u2018]/g, "'")
    .replace(/\s+/g, ' ')
    .trim();

/** Les mots d'un texte normalisé (lettres et chiffres ; l'apostrophe sépare : « l'enregistrement » → l, enregistrement). */
export const wordsOf = (normalized: string): string[] => normalized.split(/[^\p{L}\p{N}]+/u).filter((word) => word.length > 0);

const ARABIC = /[\u0600-\u06ff]/;

const stemMatchesWord = (stem: string, word: string): boolean => {
  if (stem.endsWith('$')) {
    return word === stem.slice(0, -1);
  }
  if (ARABIC.test(stem)) {
    return word.includes(stem);
  }
  return word.startsWith(stem);
};

/** Vrai quand le motif trouve ses groupes dans les mots (sans accents, minuscules) du nom. */
export const matchesPattern = (pattern: UiRiskPattern, words: readonly string[]): boolean =>
  pattern.every((group) => group.some((stem) => words.some((word) => stemMatchesWord(stem, word))));

const FR: UiRiskLangTable = {
  forbidden: [
    [['supprim', 'effac']],
    [['ecarter', 'ecartez']],
    [['archiver', 'archivez']],
    [['revoqu']],
    [['payer', 'payez', 'paiement', 'paye$']],
    [['acheter', 'achetez', 'achat']],
    [['deposer', 'deposez', 'depose$']],
    [['accepter', 'acceptez', 'accept$'], ['version', 'versions', 'modele']],
    [['approuv'], ['porte', 'portes']],
    [['promouv']],
    [['deconnect']],
    [['locataire', 'locataires']],
    [['modifier', 'modifiez', 'changer', 'changez', 'attribuer', 'accorder', 'gerer'], ['droit', 'droits', 'permission', 'permissions', 'habilitation', 'habilitations']],
    [['cle$', 'cles$', 'secret', 'secrets', 'jeton', 'jetons', 'token', 'tokens']],
  ],
  confirm: [
    [['enregistr', 'sauvegard', 'valid', 'envoy', 'soumet', 'soumis']],
    [['creer', 'creez', 'creation']],
    [['produire', 'produis', 'produisez']],
    [['lanc', 'demarr', 'gener', 'publi', 'confirm', 'accept']],
  ],
};

const EN: UiRiskLangTable = {
  forbidden: [
    [['delete', 'remove', 'erase', 'discard', 'dismiss', 'revoke']],
    [['archive$', 'archiving']],
    [['pay$', 'payment', 'payments', 'purchase', 'buy$', 'checkout']],
    [['deposit']],
    [['push', 'commit', 'deliver'], ['repo', 'repository']],
    [['accept'], ['version', 'versions', 'model']],
    [['approve'], ['gate', 'gates']],
    [['promote']],
    [['logout', 'signout']],
    [['sign', 'log'], ['out$']],
    [['tenant', 'tenants']],
    [['edit', 'change', 'modify', 'grant', 'assign', 'manage'], ['permission', 'permissions', 'right$', 'rights$', 'role$', 'roles$']],
    [['secret', 'secrets', 'token', 'tokens', 'credential', 'credentials']],
    [['api'], ['key', 'keys']],
  ],
  confirm: [
    [['save', 'submit', 'send', 'valid', 'confirm', 'accept']],
    [['create', 'produce', 'launch', 'generate', 'publish', 'run$', 'start']],
  ],
};

const DE: UiRiskLangTable = {
  forbidden: [
    [['loschen', 'entfern', 'verwerf', 'widerruf']],
    [['archivier']],
    [['bezahl', 'zahlung', 'kaufen', 'kauf$', 'erwerb']],
    [['hinterleg', 'einzahl']],
    [['hochlad', 'pushen', 'ablegen'], ['repository', 'repo$']],
    [['akzeptier', 'ubernehm'], ['version', 'versionen', 'modell']],
    [['genehmig', 'freigeb'], ['gate', 'tor', 'freigabe']],
    [['befordern']],
    [['abmeld', 'ausloggen', 'abmelden']],
    [['mandant', 'mandanten']],
    [['andern', 'bearbeit', 'vergeb', 'zuweis'], ['berechtigung', 'berechtigungen', 'recht', 'rechte', 'rolle', 'rollen']],
    [['geheim', 'schlussel', 'token', 'zugangsdaten']],
  ],
  confirm: [
    [['speicher', 'sichern', 'senden', 'sende$', 'absend', 'einreich', 'validier', 'bestatig', 'akzeptier']],
    [['erstell', 'erzeug', 'produzier', 'starte', 'start$', 'gener', 'veroffentlich']],
  ],
};

const IT: UiRiskLangTable = {
  forbidden: [
    [['elimin', 'cancella', 'rimuov', 'scarta', 'scartare', 'revoc']],
    [['archivi']],
    [['paga', 'pagamento', 'pagare', 'acquist', 'compra']],
    [['deposit']],
    [['accett'], ['versione', 'versioni', 'modello']],
    [['approv'], ['gate', 'porta', 'porte']],
    [['promuov']],
    [['disconnet', 'esci$', 'logout']],
    [['tenant', 'inquilin']],
    [['modific', 'cambia', 'assegna', 'conced'], ['permess', 'diritt', 'ruol']],
    [['segret', 'chiave', 'chiavi', 'token']],
  ],
  confirm: [
    [['salv', 'invia', 'invio$', 'sottomett', 'convalid', 'valid', 'conferm', 'accett']],
    [['crea$', 'creare', 'produc', 'produr', 'avvia', 'lancia', 'genera', 'pubblic']],
  ],
};

const AR: UiRiskLangTable = {
  forbidden: [
    [['حذف', 'مسح', 'ازالة', 'استبعاد', 'ارشف', 'سحب']],
    [['دفع', 'سداد', 'شراء', 'اشتر', 'ايداع']],
    [['خروج']],
    [['قبول'], ['اصدار', 'نسخة', 'نموذج']],
    [['اعتماد', 'موافقة'], ['بوابة']],
    [['ترقية', 'ترقي']],
    [['مستاجر']],
    [['تغيير', 'تعديل', 'منح'], ['صلاحيات', 'صلاحية', 'حقوق']],
    [['مفتاح', 'مفاتيح', 'رمز الوصول', 'الرمز المميز']],
  ],
  confirm: [
    [['حفظ', 'ارسال', 'تقديم', 'تاكيد', 'تحقق', 'قبول']],
    [['انشاء', 'انتاج', 'اطلاق', 'ابدا', 'توليد', 'نشر']],
  ],
};

/** La table, par langue (exportée pour les tests ×5). */
export const UI_RISK_TABLE: Readonly<Record<'fr' | 'en' | 'de' | 'it' | 'ar', UiRiskLangTable>> = { fr: FR, en: EN, de: DE, it: IT, ar: AR };

const ALL_LANGS = Object.values(UI_RISK_TABLE);

/**
 * Noms qui ne s'écrivent jamais (Suivant, Précédent, Fermer, Annuler, Rechercher, Filtrer, Ouvrir) : ils restent libres même dans un formulaire
 * modifié par Géode ou pour un bouton de type `submit` (la garde « formulaire modifié » ne s'applique pas à eux).
 */
const FREE_PATTERNS: readonly UiRiskPattern[] = [
  [['suivant', 'precedent', 'retour', 'fermer', 'annuler', 'rechercher', 'recherche', 'filtr', 'ouvrir', 'voir', 'afficher', 'masquer', 'replier', 'deplier']],
  [['next', 'previous', 'back$', 'close', 'cancel', 'search', 'filter', 'open', 'view', 'show', 'hide', 'collapse', 'expand']],
  [['weiter', 'zuruck', 'schlie', 'abbrech', 'such', 'filter', 'offnen', 'anzeigen', 'ansehen']],
  [['avanti', 'successiv', 'indietro', 'precedente', 'chiudi', 'annulla', 'cerca', 'filtra', 'apri', 'mostra', 'vedi']],
  [['التالي', 'السابق', 'رجوع', 'اغلاق', 'الغاء', 'بحث', 'تصفية', 'فتح', 'عرض']],
];

/** Vrai quand le nom (normalisé) est celui d'un geste qui n'écrit jamais. */
export const isNeverWritingName = (normalizedName: string): boolean => {
  const words = wordsOf(normalizedName);
  return FREE_PATTERNS.some((pattern) => matchesPattern(pattern, words));
};

/** Niveau d'un NOM seul (sans lire l'élément) : `forbidden` d'abord, puis `confirm`, sinon `free`. */
export const riskOfName = (name: string): UiRisk => {
  const words = wordsOf(normalizeUiText(name));
  if (words.length === 0) {
    return 'free';
  }
  if (ALL_LANGS.some((table) => table.forbidden.some((pattern) => matchesPattern(pattern, words)))) {
    return 'forbidden';
  }
  if (ALL_LANGS.some((table) => table.confirm.some((pattern) => matchesPattern(pattern, words)))) {
    return 'confirm';
  }
  return 'free';
};

/** Ce que la lecture de l'élément apporte à la décision (rien d'autre n'entre dans la table). */
export interface UiRiskFacts {
  /** `data-voice` de l'élément ou d'un ancêtre : `forbidden`, `confirm`, ou absent. */
  dataVoice?: string | null;
  /** L'élément est situé dans une `Modal` de variante `destructive`. */
  inDestructiveModal?: boolean;
  /** Rôle de l'élément : la table ne s'applique qu'aux boutons, éléments de menu et liens (un lien n'écrit jamais : `forbidden` seulement). */
  role?: string | null;
  /** Bouton qui soumet un formulaire (type `submit`, ou bouton sans type dans un `<form>`). */
  submits?: boolean;
  /** Le formulaire (ou le dialogue) de l'élément a été modifié par Géode : tout bouton y est confirmé, sauf les gestes qui n'écrivent jamais. */
  touchedForm?: boolean;
}

export interface UiRiskDecision {
  risk: UiRisk;
  reason: UiRiskReason;
}

/**
 * Niveau d'un geste sur un élément à partir de son nom accessible et des faits lus : `data-voice`, `Modal` destructive, table, bouton qui soumet
 * un formulaire modifié par Géode (confirmé), bouton `submit` (confirmé tant que son nom n'est pas un geste qui n'écrit jamais).
 */
export const classifyUiGesture = (name: string, facts: UiRiskFacts = {}): UiRiskDecision => {
  const dataVoice = (facts.dataVoice ?? '').trim().toLowerCase();
  if (dataVoice === 'forbidden') {
    return { risk: 'forbidden', reason: 'data-voice' };
  }
  if (facts.inDestructiveModal) {
    return { risk: 'forbidden', reason: 'modal' };
  }
  const role = facts.role ?? 'button';
  const tabled = role === 'button' || role === 'menuitem' || role === 'link';
  const rawByName = tabled ? riskOfName(name) : 'free';
  // Un lien (ou un onglet, une ligne…) n'écrit jamais : « Validations » ou « Dépôts » sont des pages, pas des gestes.
  const byName = rawByName === 'confirm' && role === 'link' ? 'free' : rawByName;
  if (byName === 'forbidden') {
    return { risk: 'forbidden', reason: 'table' };
  }
  if (dataVoice === 'confirm') {
    return { risk: 'confirm', reason: 'data-voice' };
  }
  if (byName === 'confirm') {
    return { risk: 'confirm', reason: 'table' };
  }
  // Un bouton qui soumet un formulaire, ou tout bouton d'un formulaire que Géode a modifié, n'écrit pas sans confirmation (sauf Suivant, Fermer, Annuler…).
  if ((facts.submits || facts.touchedForm) && (role === 'button' || role === 'menuitem') && !isNeverWritingName(normalizeUiText(name))) {
    return { risk: 'confirm', reason: facts.touchedForm ? 'touched-form' : 'submit' };
  }
  return { risk: 'free', reason: 'default' };
};
